<template>
  <div class="show_all_content_wrapper">
    <main>
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>

        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.orderNum')"
                v-model.trim="filterOptions.id"
              />

              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.userName')"
                v-model.trim="filterOptions.user_name"
              />

              <base-select-input
                col="6"
                :optionsList="donationStatuses"
                :placeholder="$t('PLACEHOLDERS.orderStatus')"
                v-model="filterOptions.status"
              />

              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.from_date')"
                v-model="filterOptions.from"
              />

              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.to_date')"
                v-model="filterOptions.to"
              />
            </div>

            <div class="btns_wrapper">
              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.search") }}</span>
                </template>
                <span
                  class="submit_btn"
                  @click="submitFilterForm"
                  :disabled="loading"
                >
                  <i class="fal fa-search"></i>
                </span>
              </a-tooltip>

              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.rseet_search") }}</span>
                </template>
                <span
                  class="reset_btn"
                  :disabled="loading"
                  @click="resetFilter"
                >
                  <i class="fal fa-redo"></i>
                </span>
              </a-tooltip>
            </div>
          </form>
        </div>
      </div>

      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.orders") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>

      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:no-data>
          {{
            hasActiveFilters ? $t("TABLES.noDataMatched") : $t("TABLES.noData")
          }}
        </template>

        <template v-slot:[`item.serialNumber`]="{ item }">
          <p
            class="blue-grey--text text--darken-1 fs-3"
            v-if="!item.serialNumber"
          >
            -
          </p>
          <p v-else>{{ item.serialNumber }}</p>
        </template>

        <template v-slot:[`item._status`]="{ item }">
          <v-chip>
            {{ item._status || "-" }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip
              placement="bottom"
              v-if="$can('donations show', 'donations')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="bottom"
              v-if="$can('donations invoice', 'donations')"
            >
              <template slot="title">
                <span>{{ $t("PLACEHOLDERS.pdf") }}</span>
              </template>
              <button class="btn_show" @click="showInvoice(item)">
                <i class="fas fa-file-pdf"></i>
              </button>
            </a-tooltip>

            <template
              v-if="
                !$can('donations show', 'donations') &&
                !$can('donations invoice', 'donations')
              "
            >
              <i
                class="fal fa-lock-alt fs-5 blue-grey--text text--darken-1"
              ></i>
            </template>
          </div>
        </template>
      </v-data-table>
    </main>

    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllDonations",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    donationStatuses() {
      return [
        { id: 1, name: this.$t("PLACEHOLDERS.new"), value: "new" },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.assigned_to_supervisor"),
          value: "assigned_to_supervisor",
        },
        {
          id: 3,
          name: this.$t("PLACEHOLDERS.in_progress"),
          value: "in_progress",
        },
        {
          id: 4,
          name: this.$t("PLACEHOLDERS.on_the_way"),
          value: "on_the_way",
        },
        { id: 5, name: this.$t("PLACEHOLDERS.completed"), value: "completed" },
      ];
    },

    hasActiveFilters() {
      return !!(
        this.filterOptions.id ||
        this.filterOptions.user_name ||
        this.filterOptions.status ||
        this.filterOptions.from ||
        this.filterOptions.to
      );
    },
  },

  data() {
    return {
      loading: false,

      filterFormIsActive: false,
      filterOptions: {
        id: null,
        user_name: null,
        status: null,
        from: null,
        to: null,
      },

      tableHeaders: [
        {
          text: this.$t("TABLES.Clients.serialNumber"),
          value: "serialNumber",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.orderNum"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.clientName"),
          value: "donor_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.subscriptions.Totalorder"),
          value: "total",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.orderStatus"),
          value: "_status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.created_at"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Clients.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],

      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
    };
  },

  watch: {
    "$route.query.page"() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },

    "filterOptions.id"(value) {
      if (value && value.length > 100) {
        this.filterOptions.id = value.slice(0, 100);
      }
    },

    "filterOptions.user_name"(value) {
      if (value && value.length > 100) {
        this.filterOptions.user_name = value.slice(0, 100);
      }
    },
  },

  methods: {
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/donations/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.id = null;
      this.filterOptions.user_name = null;
      this.filterOptions.status = null;
      this.filterOptions.from = null;
      this.filterOptions.to = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/donations/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    async setTableRows() {
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "GET",
          url: "donations",
          params: {
            page: this.paginations.current_page,
            id: this.filterOptions.id,
            user_name: this.filterOptions.user_name,
            status: this.filterOptions.status?.value,
            from: this.filterOptions.from,
            to: this.filterOptions.to,
          },
        });

        const rows = res.data?.data?.data || [];
        rows.forEach((item, index) => {
          item.serialNumber =
            (this.paginations.current_page - 1) *
              this.paginations.items_per_page +
            index +
            1;
        });

        this.tableRows = rows;
        this.paginations.last_page = res.data?.data?.meta?.last_page || 1;
        this.paginations.items_per_page = res.data?.data?.meta?.per_page || 10;
      } catch (error) {
        this.tableRows = [];
        this.$message.error(
          error?.response?.data?.message || this.$t("TABLES.noData")
        );
      } finally {
        this.loading = false;
      }
    },

    showItem(item) {
      this.$router.push({ path: `/donations/show/${item.id}` });
    },

    showInvoice(item) {
      this.$router.push({ path: `/donations/invoice/${item.id}` });
    },
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });

    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }

    this.setTableRows();
  },
};
</script>
